import { List, useTable } from "@refinedev/antd";
import { MinusSquareOutlined, PlusSquareOutlined } from "@ant-design/icons";
import { Button, Select, Space, Table, Typography } from "antd";
import dayjs from "dayjs";

import { AUDIT_ACTIONS, AUDIT_TARGETS, COPY_TOOLTIPS, DATE_TIME_FORMAT } from "../labels";
import type { AuditEntry } from "../types";

const actionOptions = Object.entries(AUDIT_ACTIONS).map(([value, label]) => ({ value, label }));

/** /audit-log: every office change, newest first (permission `audit.view`). */
export function AuditLogPage() {
  const { tableProps, setFilters, filters } = useTable<AuditEntry>({
    resource: "audit-log",
    pagination: { mode: "server", pageSize: 25 },
    syncWithLocation: true,
  });
  const action = filters.find((filter) => "field" in filter && filter.field === "action");

  return (
    <List title="Kayıtlar">
      <Space style={{ marginBottom: 16 }}>
        <Select
          allowClear
          placeholder="İşlem"
          style={{ minWidth: 240 }}
          options={actionOptions}
          value={action?.value as string | undefined}
          onChange={(value?: string) =>
            setFilters([{ field: "action", operator: "eq", value }], "replace")
          }
        />
      </Space>
      <Table
        {...tableProps}
        rowKey="id"
        scroll={{ x: "max-content" }}
        expandable={{
          rowExpandable: (entry) => entry.details !== null,
          // Ant Design's own expand button has no accessible name.
          expandIcon: ({ expanded, expandable, onExpand, record }) =>
            expandable ? (
              <Button
                type="text"
                size="small"
                aria-label={expanded ? "Ayrıntıyı gizle" : "Ayrıntıyı göster"}
                icon={expanded ? <MinusSquareOutlined /> : <PlusSquareOutlined />}
                onClick={(event) => onExpand(record, event)}
              />
            ) : null,
          expandedRowRender: (entry) => (
            <Typography.Text code style={{ whiteSpace: "pre-wrap" }}>
              {JSON.stringify(entry.details, null, 2)}
            </Typography.Text>
          ),
        }}
      >
        <Table.Column<AuditEntry>
          title="Tarih"
          dataIndex="createdAt"
          render={(value: string) => dayjs(value).format(DATE_TIME_FORMAT)}
        />
        <Table.Column<AuditEntry>
          title="Ekip üyesi"
          dataIndex="staffUser"
          render={(staffUser: AuditEntry["staffUser"]) => staffUser?.name ?? "Sunucu komutu"}
        />
        <Table.Column<AuditEntry>
          title="İşlem"
          dataIndex="action"
          render={(value: string) => AUDIT_ACTIONS[value] ?? value}
        />
        <Table.Column<AuditEntry>
          title="Hedef"
          dataIndex="targetType"
          render={(value: string, entry) => (
            <Space direction="vertical" size={0}>
              <span>{AUDIT_TARGETS[value] ?? value}</span>
              <Typography.Text
                type="secondary"
                copyable={{ text: entry.targetId, tooltips: COPY_TOOLTIPS }}
              >
                {entry.targetId.slice(0, 8)}
              </Typography.Text>
            </Space>
          )}
        />
      </Table>
    </List>
  );
}
